import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";
import RightTrapezoidDiagram
  from "../../../../components/diagrams/RightTrapezoidDiagram";

const DEC_TOL = 0.01;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// wyciągnięcie największego kwadratu: n = s^2 * r
function squareFactorOut(n) {
  let r = Math.max(0, Math.round(n));
  let s = 1;
  for (let p = 2; p * p <= r; p++) {
    while (r % (p * p) === 0) {
      r = Math.floor(r / (p * p));
      s *= p;
    }
  }
  return [s, r];
}

export function generateTask() {
  // a — górna, b — dolna; wymagamy b > a (trapez prostokątny z „dłuższym dołem”)
  const As = [6, 7, 8, 9, 10, 11, 12];
  const Bs = [8, 9, 10, 11, 12, 13, 14, 15, 16];
  const Hs = [3, 4, 5, 6, 7, 8, 9];

  for (let t = 0; t < 200; t++) {
    const a = pick(As);
    const candidatesB = Bs.filter((v) => v > a);
    if (!candidatesB.length) continue;
    const b = pick(candidatesB);
    const h = pick(Hs);
    return { type: "TrapezoidRightOtherLegTask", data: { a, b, h} };
  }
  return { type: "TrapezoidRightOtherLegTask", data: { a: 8, b: 11, h: 6 } };
}

export function renderTask({ data }) {
  const { a, b, h } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8, display: "grid", gap: 10 }}>
      <div>
        Dany jest trapez <b>prostokątny</b> o podstawach{" "}
        <InlineMath math={`a=${a}`} /> (górna),{" "}
        <InlineMath math={`b=${b}`} /> (dolna) i wysokości{" "}
        <InlineMath math={`h=${h}`} />. Oblicz długość <b>drugiego ramienia</b>{" "}
        (skośnego boku) <InlineMath math={"c"} />.
      </div>

      {/* Rysunek w treści: z wartościami, ramię c wyróżnione */}
      <div style={{ display: "grid", placeItems: "center", marginTop: 6 }}>
        <div style={{ width: "min(100%, 420px)" }}>
          <RightTrapezoidDiagram
            a={a}
            b={b}
            h={h}
            showValues={true}
            highlightLeg
            legLabel="c"
            legColor="#8b5cf6"
          />
        </div>
      </div>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="c"
      placeholder="np. 5√2"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { a, b, h } = data;
  // w trójkącie z ramieniem c przyprostokątne to h oraz (b−a)
  const expected = Math.sqrt((b - a) * (b - a) + h * h);

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= DEC_TOL;
}

/* --- odpowiedź: c = s√r (bez „1√…”) --- */
export const renderAnswer = ({ data }) => {
  const { a, b, h } = data;
  const R = (b - a) * (b - a) + h * h;
  const [s, r] = squareFactorOut(R);
  if (r === 1) return <InlineMath math={`c=${s}`} />;
  if (s === 1) return <InlineMath math={`c=\\sqrt{${r}}`} />;
  return <InlineMath math={`c=${s}\\,\\sqrt{${r}}`} />;
};

export function renderExplanation({ data }) {
  const { a, b, h } = data;

  // stałe marginesy dla rysunku (BYŁY brakujące)
  const marginTopPx = 14;
  const marginBottomPx = 6;

  const diff = b - a;
  const diff2 = diff * diff;
  const h2 = h * h;
  const R = diff2 + h2;
  const [s, r] = squareFactorOut(R);
  const cLatex =
    r === 1 ? `${s}` : s === 1 ? `\\sqrt{${r}}` : `${s}\\,\\sqrt{${r}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, display: "grid", gap: 10 }}>
      <div><strong>Krok 1.</strong> Schemat (z danymi):</div>
      <div style={{ marginTop: marginTopPx, marginBottom: marginBottomPx }}>
        <div style={{ display: "grid", placeItems: "center" }}>
          <div style={{ width: "min(100%, 420px)" }}>
            <RightTrapezoidDiagram
              a={a}
              b={b}
              h={h}
              showValues={true}
              highlightLeg
              legLabel="c"
              legColor="#8b5cf6"
              showAuxRightTriangle   // pokazujemy h i (b−a), by było widać trójkąt prostokątny
            />
          </div>
        </div>
      </div>

      <div>
        <strong>Krok 2.</strong> Trójkąt z ramieniem <InlineMath math={"c"} /> ma
        przyprostokątne <InlineMath math={"h"} /> oraz{" "}
        <InlineMath math={"b-a"} />:
      </div>
      <BlockMath math={"c^2=h^2+(b-a)^2\\quad\\Rightarrow\\quad c=\\sqrt{h^2+(b-a)^2}"} />

      <div><strong>Krok 3.</strong> Podstawiamy i liczymy krok po kroku:</div>
      <BlockMath math={`c=\\sqrt{${h}^2+(${b}-${a})^2}`} />
      <BlockMath math={`c=\\sqrt{${h2}+${diff}^2}`} />
      <BlockMath math={`c=\\sqrt{${h2}+${diff2}}=\\sqrt{${R}}`} />
      <BlockMath math={`c=${cLatex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(raw, "c")} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
